import { useFlags } from '@features/feature-flags';
import { cn } from '@lib/cn';
import { initials } from '@lib/initials';
import { ROLE_LABEL } from '@lib/operator';
import { Link, useLocation } from 'react-router';

import type { Operator } from '@lib/operator';

import { NAV_GROUPS, type NavItem } from './nav-items';

function isActive(pathname: string, href: string): boolean {
    return pathname === href || pathname.startsWith(`${href}/`);
}

function NavRow({ item, active }: { item: NavItem; active: boolean }) {
    const Icon = item.icon;
    const inner = (
        <>
            <Icon className={cn('size-[18px]', active ? 'opacity-100' : 'opacity-90')} />
            <span>{item.label}</span>
            {item.count != null && (
                <span
                    className={cn(
                        'ml-auto rounded-full px-2 py-0.5 text-[11px] font-semibold',
                        active ? 'bg-brand-100 text-brand-700' : 'bg-gray-100 text-gray-500',
                    )}
                >
                    {item.count}
                </span>
            )}
            {item.disabled && (
                <span className="ml-auto text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                    Soon
                </span>
            )}
        </>
    );

    const base =
        'flex w-full items-center gap-3 rounded-lg px-2.5 py-2 text-left text-[13.5px] font-medium transition-colors';

    if (item.disabled) {
        return (
            <span className={cn(base, 'cursor-not-allowed text-gray-400')} aria-disabled="true">
                {inner}
            </span>
        );
    }

    return (
        <Link
            to={item.href}
            className={cn(
                base,
                active
                    ? 'bg-brand-50 font-semibold text-brand-700'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900',
            )}
            aria-current={active ? 'page' : undefined}
        >
            {inner}
        </Link>
    );
}

export function Sidebar({ operator }: { operator: Operator }) {
    const { pathname } = useLocation();
    // Shares the flags list query with the flags screen rather than issuing its
    // own — same key, so navigating between screens does not refetch, and a
    // flag created in the dialog updates this badge through the same
    // invalidation.
    const flags = useFlags();

    return (
        <nav className="flex h-full flex-col gap-1 p-3" aria-label="Primary">
            {NAV_GROUPS.map((group, i) => (
                <div key={group.label ?? `group-${i}`} className="mb-1.5">
                    {group.label && (
                        <div className="px-2.5 pb-1.5 pt-3 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                            {group.label}
                        </div>
                    )}
                    {group.items.map((item) => (
                        <NavRow
                            key={item.href}
                            item={
                                item.href === '/flags' && flags.data
                                    ? { ...item, count: flags.data.length }
                                    : item
                            }
                            active={isActive(pathname, item.href)}
                        />
                    ))}
                </div>
            ))}

            {/*
             * The signed-in employee, from Atelier's shared session, and the
             * Backoffice tier the API resolved for them (NOXSCRUM-1436).
             *
             * This line used to read "Veevart employee" because no role
             * existed. Now that one does, showing it is what makes a disabled
             * button legible — an operator who cannot flip a flag can see, in
             * one place, that they are read-only rather than that the app is
             * broken.
             */}
            <div className="mt-auto flex items-center gap-2.5 border-t border-gray-100 px-2.5 pt-3">
                <span className="flex size-7 items-center justify-center rounded-full border border-brand-200 bg-brand-50 text-[10px] font-semibold text-brand-700">
                    {initials(operator.name)}
                </span>
                <div className="min-w-0 text-xs leading-tight">
                    <div className="truncate font-semibold text-gray-900">{operator.name}</div>
                    <div className="truncate text-gray-500">{ROLE_LABEL[operator.role]}</div>
                </div>
            </div>
        </nav>
    );
}
